import { useMemo, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { TextField } from '@/components/ui/TextField'
import { Callout } from '@/components/ui/Callout'
import { useAuth } from '@/lib/auth/use-auth'
import { AUTH_ERROR_MESSAGE } from '@/lib/auth/auth-context'
import { AuthError, MIN_PASSWORD_LENGTH } from '@/lib/auth'

interface Rule {
  label: string
  test: (value: string) => boolean
}

/**
 * Client-side checks give immediate feedback only. The real password policy
 * must be enforced on the server — see question C9 in
 * docs/02-CAU-HOI-CAN-TRA-LOI.md.
 */
const RULES: Rule[] = [
  { label: `${MIN_PASSWORD_LENGTH}+ characters`, test: (v) => v.length >= MIN_PASSWORD_LENGTH },
  { label: 'Upper and lower case', test: (v) => /[a-z]/.test(v) && /[A-Z]/.test(v) },
  { label: 'One digit', test: (v) => /\d/.test(v) },
  { label: 'One special character', test: (v) => /[^A-Za-z0-9]/.test(v) },
]

export function ChangePasswordPage() {
  const { changePassword, session } = useAuth()
  const navigate = useNavigate()

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const forced = session?.user.mustChangePassword ?? false
  const ruleState = useMemo(() => RULES.map((r) => r.test(newPassword)), [newPassword])
  const allRulesPass = ruleState.every(Boolean)
  const mismatch = confirmPassword.length > 0 && confirmPassword !== newPassword

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)

    if (!allRulesPass) {
      setError('Password does not meet the requirements.')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    setSubmitting(true)
    try {
      await changePassword({ currentPassword, newPassword })
      setDone(true)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err) {
      setError(
        err instanceof AuthError ? AUTH_ERROR_MESSAGE[err.code] : AUTH_ERROR_MESSAGE.unknown,
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mx-auto max-w-[520px]">
      <h1 className="t-title-lg text-ink">Change password</h1>

      {forced && !done && (
        <Callout tone="warning" className="mt-6">
          Password change required before continuing.
        </Callout>
      )}

      {done ? (
        <div className="mt-6">
          <Callout tone="info">Password changed.</Callout>
          <Button className="mt-6" onClick={() => navigate('/')}>
            Continue
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate className="mt-8 space-y-5">
          {error && <Callout tone="error">{error}</Callout>}

          <TextField
            label="Current password"
            type="password"
            autoComplete="current-password"
            revealable
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            disabled={submitting}
          />

          <TextField
            label="New password"
            type="password"
            autoComplete="new-password"
            revealable
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={submitting}
          />

          <TextField
            label="Confirm new password"
            type="password"
            autoComplete="new-password"
            revealable
            value={confirmPassword}
            error={mismatch ? 'Passwords do not match' : undefined}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={submitting}
          />

          <ul className="space-y-2 rounded-[16px] bg-surface-soft p-4">
            {RULES.map((rule, index) => (
              <RuleRow key={rule.label} label={rule.label} passed={ruleState[index]} />
            ))}
          </ul>

          <div className="flex gap-3">
            <Button type="submit" loading={submitting}>
              Change password
            </Button>
            {!forced && (
              <Button type="button" variant="secondary" onClick={() => navigate(-1)}>
                Cancel
              </Button>
            )}
          </div>
        </form>
      )}
    </div>
  )
}

function RuleRow({ label, passed }: { label: string; passed: boolean }) {
  return (
    <li className="flex items-center gap-2.5">
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        {passed ? (
          <path
            d="m3.5 8.5 3 3 6-6"
            stroke="#05b169"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : (
          <circle cx="8" cy="8" r="3" fill="#a8acb3" />
        )}
      </svg>
      <span className={`t-body-sm ${passed ? 'text-ink' : 'text-muted'}`}>{label}</span>
    </li>
  )
}
